import React, { useState } from "react";
import { EllipsisVertical, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";

export interface Summary {
  id: string;
  title: string;
  type: string;
  created_at: string;
}

interface MySummariesListProps {
  summaries: Summary[];
  onRename?: (id: string, newTitle: string) => void;
  onDelete?: (id: string) => void;
  onClick?: (id: string) => void;
}

export const MySummariesList: React.FC<MySummariesListProps> = ({
  summaries,
  onRename,
  onDelete,
  onClick,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");

  const handleStartEdit = (e: React.MouseEvent, summary: Summary) => {
    e.stopPropagation();
    setEditingId(summary.id);
    setEditTitle(summary.title);
  };

  const handleSaveEdit = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!editTitle.trim() || !editingId) return;
    onRename?.(editingId, editTitle.trim());
    setEditingId(null);
    setEditTitle("");
  };

  const handleCancelEdit = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setEditingId(null);
    setEditTitle("");
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onDelete?.(id);
  };

  if (summaries.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 pt-4">
      <h3 className="text-sm font-medium text-muted-foreground">My Summaries</h3>
      <div className="flex flex-col gap-3">
        {summaries.map((summary) => (
          <Card
            key={summary.id}
            className="group relative cursor-pointer rounded-2xl border border-border bg-card p-4 shadow-sm transition-all duration-200 hover:bg-accent/40"
            onClick={() => editingId !== summary.id && onClick?.(summary.id)}
          >
            <div className="flex items-start justify-between gap-6">
              <div className="min-w-0 flex-1 space-y-2">
                {editingId === summary.id ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveEdit(e);
                        if (e.key === "Escape") handleCancelEdit(e);
                      }}
                      className="h-8 text-sm"
                      autoFocus
                    />
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={handleSaveEdit}>
                      Save
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex min-w-0 items-center gap-2 text-base font-medium text-foreground">
                    <span className="truncate">{summary.title}</span>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-1">
                <div className="truncate rounded-full bg-premium/10 px-2 py-1 text-xs text-premium">
                    {summary.type}
                  </div>
                </div>
              </div>
              
              {editingId !== summary.id && (
                <div className="relative flex-shrink-0">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <EllipsisVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end" className="z-50 bg-popover">
                      <DropdownMenuItem onClick={(e) => handleStartEdit(e, summary)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Rename
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      <DropdownMenuItem 
                        onClick={(e) => handleDelete(e, summary.id)} 
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};